
import { Component, Emit, Prop, Vue } from 'vue-property-decorator';
import { UploadFileType } from '@/enums';
import { UploadFileResponse } from '@/api/typing';
import { Api } from '@/util/api';
import { ActionUtils } from '@/util/action';

@Component({})
export default class UploadFileInput extends Vue {
  @Prop({ type: String, required: true })
  type!: UploadFileType;

  @Prop({ type: Array, required: true, default: () => [] })
  files: UploadFileResponse[] = [];

  // image/png, image/jpeg
  @Prop({ type: String, required: false })
  acceptedFiles?: string;

  @Prop({ type: String, required: false })
  tip?: string;

  @Prop({ type: Boolean, required: false, default: false })
  multiple?: boolean;

  @Prop({ type: String, required: false, default: 'picture-card' })
  listType?: 'picture-card' | 'text' | 'picture';

  @Prop({ type: Number, required: false })
  limit?: number;

  @Prop({ type: String, required: false })
  exceedTip?: string;

  uploadingFiles = new Set<string>();

  @Emit('uploading-change')
  onUploadingChange(uploading: boolean) {
    return uploading;
  }

  updateUploadingStatus() {
    const isUploading = this.uploadingFiles.size > 0;
    this.onUploadingChange(isUploading);
  }

  onUploadProgress(event: any, file: any) {
    const fileId = file.uid || file.name;
    if (!this.uploadingFiles.has(fileId)) {
      this.uploadingFiles.add(fileId);
      this.updateUploadingStatus();
    }
  }

  @Emit('update:files')
  onUploadFiles(res: any, file: File, files: UploadFileResponse[]) {
    const fileId = (file as any).uid || (file as any).name;
    this.uploadingFiles.delete(fileId);
    this.updateUploadingStatus();
    return files;
  }

  onUploadError(err: any, file: any) {
    const fileId = file.uid || file.name;
    this.uploadingFiles.delete(fileId);
    this.updateUploadingStatus();
  }

  get uploadFileHeaders() {
    return {
      Token: localStorage.getItem('token'),
    };
  }

  // 自定义上传方法
  async customUpload(options: any) {
    const { file, onProgress, onSuccess, onError } = options;
    const fileId = (file as any).uid || (file as any).name;
    try {
      // 标记上传中（有些情况下 progress 事件会较晚才触发）
      if (!this.uploadingFiles.has(fileId)) {
        this.uploadingFiles.add(fileId);
        this.updateUploadingStatus();
      }

      // 每个文件独立获取 token + url，避免并发覆盖/竞态
      const tokenResp = await Api.current.coreApi.getUploadFileTokenUsingPOST({
        type: this.type,
        contentType: file.type,
        originalFilename: file.name,
      });

      const uploadedFile = tokenResp.data.uploadedFile;
      const uploadToken = tokenResp.data.uploadToken;
      const uploadUrl = tokenResp.data.uploadUrl;

      if (!uploadedFile || !uploadToken || !uploadUrl) {
        throw new Error('Invalid upload response');
      }

      // 让后续保存/取 fileId 能拿到完整信息
      Object.assign(file, {
        response: {
          file: uploadedFile,
        },
        url: uploadedFile.url,
      });

      const formData = new FormData();
      formData.append('key', uploadedFile.key);
      formData.append('token', uploadToken);
      formData.append('file', file);

      const xhr = new XMLHttpRequest();

      // 上传进度
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percent = (e.loaded / e.total) * 100;
          onProgress({ percent });
        }
      });

      // 上传完成
      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 204) {
          // 确保 el-upload 最终的 file.response 保留后端返回的文件信息
          const resp = { file: uploadedFile };
          (file as any).response = resp;
          onSuccess(resp, file);
        } else {
          onError(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      // 上传错误
      xhr.addEventListener('error', () => {
        onError(new Error('Upload failed'));
      });

      xhr.open('POST', uploadUrl);
      xhr.setRequestHeader('Token', localStorage.getItem('token') || '');
      xhr.send(formData);
    } catch (e) {
      // 错误时也要清理上传状态
      const fileId = (file as any).uid || (file as any).name;
      this.uploadingFiles.delete(fileId);
      this.updateUploadingStatus();
      onError(e);
    }
  }

  @Emit('update:files')
  onRemoveFile(file: File, files: UploadFileResponse[]) {
    this.files = [...files];
    return this.files;
  }

  onExceedFiles() {
    this.$message.error(this.exceedTip || `最多只允许上传${this.limit}个文件`);
  }

  onPreviewFile(file: UploadFileResponse) {
    ActionUtils.open(file.url);
  }
}
